import type { Despesa, Insumo, Produto, ProdutoComposicaoItem, Venda } from "@/lib/api";
import { calcularCustoTotalComposicao } from "@/lib/produtoMetricas";

function classificacaoDespesa(c: string | undefined | null): "despesa" | "compra" | "outro" {
  if (c === "compra" || c === "outro" || c === "despesa") return c;
  return "despesa";
}

/** Total da aba Financeiro → Despesas (exclui compras, outros, pagamentos, transferências, CPV). */
export function totalDespesasFinanceiroPeriodo(despesas: Despesa[]): number {
  return despesas
    .filter((d) => classificacaoDespesa(d.classificacao) === "despesa")
    .reduce((s, d) => s + Number(d.valor || 0), 0);
}

/** Total da aba Financeiro → Compras. */
export function totalComprasFinanceiroPeriodo(despesas: Despesa[]): number {
  return despesas
    .filter((d) => classificacaoDespesa(d.classificacao) === "compra")
    .reduce((s, d) => s + Number(d.valor || 0), 0);
}

/** Lucro = Vendas − Despesas − Compras. */
export function calcularLucroDashboard(vendasTotal: number, despesas: Despesa[]): number {
  return vendasTotal - totalDespesasFinanceiroPeriodo(despesas) - totalComprasFinanceiroPeriodo(despesas);
}

export type DashboardCustoContext = {
  produtosPorId: Map<number, Produto>;
  composicaoPorProdutoId: Map<number, ProdutoComposicaoItem[]>;
  insumosPorId: Map<number, Pick<Insumo, "valorAtual">>;
};

/** Custo unitario do produto. null indica custo ausente ou incompleto; zero e um custo conhecido. */
export function resolverCustoUnitarioProduto(produtoId: number, ctx: DashboardCustoContext): number | null {
  const produto = ctx.produtosPorId.get(produtoId);
  if (!produto) return null;

  if (produto.possui_composicao) {
    const comp = ctx.composicaoPorProdutoId.get(produtoId);
    if (!comp || comp.length === 0) return null;
    if (comp.some((c) => {
      const custo = ctx.insumosPorId.get(Number(c.insumo_id))?.valorAtual;
      return custo == null || !Number.isFinite(custo) || custo < 0 ||
        !Number.isFinite(c.quantidade) || c.quantidade <= 0 || !c.unidade_base || !c.unidade;
    })) return null;
    const linhas = comp.map((c) => ({
      insumo_id: c.insumo_id,
      quantidade: String(c.quantidade),
      unidade: c.unidade ?? "un",
      unidade_base: c.unidade_base ?? "un",
    }));
    const total = calcularCustoTotalComposicao(linhas, ctx.insumosPorId);
    return total >= 0 ? total : null;
  }

  const va = produto.valorAtual;
  if (va == null || !Number.isFinite(va) || va < 0) return null;
  return va;
}

/** Soma custos conhecidos; itens sem custo suficiente nao impedem a soma dos demais. */
export function calcularCustoProdutosVendidos(vendas: Venda[], ctx: DashboardCustoContext): number | null {
  let total = 0;
  for (const v of vendas) {
    for (const it of v.itens ?? []) {
      const produtoId = Number(it.produto_id ?? 0);
      if (produtoId <= 0) continue;
      const qty = Number(it.quantidade ?? 0);
      if (!Number.isFinite(qty)) return null;
      if (qty <= 0) continue;
      const unit = resolverCustoUnitarioProduto(produtoId, ctx);
      if (unit == null) continue;
      total += unit * qty;
    }
  }
  return Math.round(total * 100) / 100;
}

export function buildDashboardCustoContext(
  produtos: Produto[],
  insumos: Insumo[],
  composicaoPorProdutoId: Record<number, ProdutoComposicaoItem[]>,
): DashboardCustoContext {
  const produtosPorId = new Map<number, Produto>();
  for (const p of produtos) produtosPorId.set(p.id, p);

  const insumosPorId = new Map<number, Pick<Insumo, "valorAtual">>();
  for (const i of insumos) insumosPorId.set(i.id, { valorAtual: i.valorAtual });

  const composicaoMap = new Map<number, ProdutoComposicaoItem[]>();
  for (const [id, items] of Object.entries(composicaoPorProdutoId)) {
    composicaoMap.set(Number(id), items);
  }

  return { produtosPorId, composicaoPorProdutoId: composicaoMap, insumosPorId };
}
